import { INVOICE_TRANSACTION_MAPPING_STATUS_VALUES } from "types/contants/invoiceTransactionMappingStatus";
import { Pagination } from "./pagination";
import * as FILE_TYPE from "constants/fileTypes";
import { Invoice } from "types/Models/invoice";

export type Comment = {
  uuid: string;
  description: string;
  created_by: CreatedBy;
  created_at: string;
};

export type GroupCategorization = {
  transaction_category_name: string;
  similar_merchant_txns_count: number;
  transaction_category_uuid: string;
  merchant_data_uuid: string;
  merchant_data_name: string;
  similar_merchant_txns_ids: string[];
  previous_assigned_category_id: string | null;
  previous_assigned_category_name: string | null;
};

type TransactionCategoryType = {
  uuid: string;
  name: string;
  identifier: number;
} | null;

export type SplitTransaction = {
  uuid: string;
  merchant: null | string;
  merchant_uuid: null | string;
  amount: number;
  category?: {
    uuid: string;
    name: string;
  };
  accrual_category: TransactionCategoryType;
  cash_category: TransactionCategoryType;
  description: string;
  status: string;
  logo: null | string;
  created_by: string;
  linked_transaction: null | string;
  invoice: InvoiceType | null;
  entity_transaction_id: string;
  classifications?: Array<{
    uuid: string;
    name: string;
    classes: Array<{
      uuid: string;
      name: string;
    }>;
  }>;
};

export type Transactions = {
  transaction: Transaction;
  cash_category_id?: string | null;
  accrual_category_id?: string | null;
  txn_merchant: null | string;
  tags: TxnTag[];
  invoices: InvoiceType[];
  running_balance?: number;
  comments: { notes: Comment[] };
  group_categorization?: GroupCategorization;
  split_transactions: null | Transaction[];
  split_transactions_merchant_count: number;
  comments_count: number;
  stripe_aggregate_entry_ledger_entry: null | {
    uuid: string;
    name: string;
    aggregate_entry_id: string;
    aggregate_entry_date: string;
  };
  has_linked_invoices?: boolean;
  has_linked_bills?: boolean;
  has_suggested_invoice_bill?: boolean;
  suggested_invoice_bill_count?: number;
  has_classes: boolean;
  has_suggestions?: boolean;
  available_amount: number;
  parent_txn: ParentTxn;
  classification_details?: Classification[];
  excluded_from_reconciliation: boolean;
  is_reconciled: boolean;
};

export type ParentTxn = {
  uuid: string;
  amount: number;
  description: string;
  date: string;
};

export type TxnTag = { name: string; uuid: string; order: number };

export type ClassificationClass = {
  uuid: string;
  name: string;
};

export type Classification = {
  uuid: string;
  name: string;
  classes: ClassificationClass[];
};

export type CreatedBy = {
  name: string;
  uuid: string;
  profile_url?: string;
};

type createdBy = {
  first_name: string;
  last_name: string;
  email: string;
  uuid: string;
  image_url: string;
};

export type TransactionResponse = {
  channel_url: string;
  private_channel_url: string;
  transactions: Transactions[];
  page_number?: number;
} & Pagination;

type TxnFrom = {
  bank_account?: {
    uuid: string;
    nickname: string;
    mask: string;
    bank_brand?: {
      name: string;
      logo_url?: string;
      connection_provider: string | null;
    };
  };
  source:
    | "JOURNAL_ENTRY"
    | "PLAID"
    | "BANK_STATEMENT"
    | "DIRECT_CONNECT"
    | "LEDGER_ENTRY";
};

export type Transaction = {
  uuid: string;
  merchant?: string;
  merchant_uuid?: string;
  amount: number;
  category?: {
    name: string;
    uuid: string;
    identifier: number;
  };
  description?: string;
  date: string;
  state: string;
  comment?: string;
  status: "EXCLUDED" | "RESTORED" | "REVIEWED" | "FOR_REVIEW";
  datetime?: string;
  memo?: string;
  pending: boolean;
  logo?: string;
  linked_transaction?: string;
  source?: "MANUAL" | "PLAID" | "CSV";
  categorisation_status:
    | "NOT_CATEGORISED"
    | "AI_CATEGORISED"
    | "MANUAL_CATEGORISED"
    | "RULE_CATEGORIZED"
    | "AI_CATEGORISED_ACCEPTED";
  created_by?: createdBy;
  from?: TxnFrom;
  ledger_entry: {
    uuid: string;
    entry_number: number;
    entry_name: string;
  } | null;
  suggested_categories: {
    uuid: string;
    name: string;
    identifier: number;
  }[];
  is_credit_card: boolean;
  journal_entry: boolean;
  ledger_entry_serial_number: string;
  classification_details?: Classification[];
  original_amount?: number;
  exchange_rate?: number;
  currency?: string;
  amount_before_edit?: number;
};

export type FileData = {
  uuid: string;
  invoice_status: INVOICE_TRANSACTION_MAPPING_STATUS_VALUES;
  invoice_uuid: string;
  name: string;
  is_previewable: boolean;
  file_type: keyof typeof FILE_TYPE;
};

export type InvoiceType = {
  uuid: string;
  file_data?: FileData;
  invoice_uuid?: string;
  invoice_number?: string;
  has_inkle_invoice?: boolean;
  has_inkle_bill?: boolean;
};

export interface TxnCategories {
  uuid: string;
  name: string;
  description: string;
  identifier: number;
  identifier_prefix: 1 | 2 | 3 | 4 | 5;
  manually_added: boolean;
  parent_id: string;
  category_type?:
    | "STANDARD"
    | "BANK_TRANSFER"
    | "BANK_ACCOUNT"
    | "PAY_DOWN_CREDIT"
    | "SUGGESTED"
    | "SUGGESTED_PARENT";
  parent_name: string;
  types?: TxnCategories[];
  category_tag?: string;
  start_date: string | null;
  opening_balance: number | null;
  topLevel?: boolean;
  is_standard_category?: boolean;
  is_system_category?: boolean;
}

export type OpeningBalanceSummary = {
  assets: number;
  liability: number;
  equity: number;
  revenue: number;
  expense: number;
  total_equity: number;
  difference: number;
};

export type BalanceSummary = {
  difference: number;
  total_credits: number;
  total_debits: number;
};

export type TxnById = {
  transaction: Transaction;
  bank_brand: {
    name: string;
    logo_url: string;
  };
  bank_account: { mask: string; uuid: string; nickname: string };
  txn_merchant: null | string;
  invoices: InvoiceType[];
};

export type GeneralLedger = {
  uuid: string;
  end_date: string;
  identifier: number;
  name: string;
  opening_balance: number;
  show_opening_balance: boolean;
  running_total: number;
  show_data: boolean;
  start_date: string;
  summary_amount: number;
  summary_count: number;
  total_credits: number;
  total_debits: number;
  transactions_count: number;
  transactions: ReportTxn[];
  types: GeneralLedger[];
  category_type: string;
  trial_balance_amount: number;
  is_debit_account: boolean;
};

export type ReportTxn = {
  uuid: string;
  amount: number;
  date: string;
  running_total: string;
  merchant: { name: string; logo: string };
  description: string;
  is_ledger_entry: boolean;
  is_credit_card: boolean;
  financial_record_type: "INVOICE" | "BILL" | null;
  from?: TxnFrom;
  is_aggregate_entry: boolean;
  aggregate_entry_count: number;
  category?: {
    name: string;
    uuid: string;
    identifier: number;
  };
};

export type BankItems = {
  bank_name: string;
  number_of_accounts: number;
  logo: string;
  last_disconnected_at: string;
  entity_id: string;
};

export type ReconcilitionItems = {
  last_reconcilied_at: string;
  entity_id: string;
};

export type FinancialClosing = {
  uuid: string;
  start_date: string;
  end_date: string;
  closing_period: string;
  status: string;
  entity_id: string;
  review_message_id: string;
};

export type Items = BankItems | ReconcilitionItems | FinancialClosing;

export type BooksActionItems = {
  action_items_count: number;
  action_items_response: {
    item_type:
      | "Connections"
      | "Reconciliation"
      | "Financial Closing"
      | "Chat Requests"
      | "ACCOUNTANT_ACCESS_REQUEST";
    items: Items[];
    items_count?: number;
  }[];
  channel_url: string;
};

export const TXN_ACCOUNT = {
  STRIPE: "STRIPE",
  EXCLUDE_STRIPE: "EXCLUDE_STRIPE",
  ALL: "ALL",
} as const;

export type TxnAccountType = keyof typeof TXN_ACCOUNT;

export type SourceFilter = {
  mask: string;
  nickname: string;
  plaid_data: {
    account_id: string;
    account_number: string;
    available_balance: number;
    balance_matches: boolean;
    current_balance: number;
    iso_currency_code: string;
    official_name: string;
    routing_number: string;
    running_balance: number;
    subtype: string;
    type: string;
  } | null;
  uuid: string;
  brand_name: string;
  connection_provider: string | null;
  is_manually_added: boolean;
};

export type ImportCOAData = {
  name: string;
  parent: string;
  identifier: number;
  identifier_prefix: number;
  opening_balance: number;
  description: string;
  depth: number;
};

type ParsedAccount = {
  description: string;
  name: string;
  opening_balance: number;
  account_type: string;
};

export type UploadCOAFileResponse = {
  entity: string;
  uploaded_csv: string;
  coa_import_state_id: string;
  bank_account_names: string[];
  credit_card_names: string[];
  coa_data: ImportCOAData[];
  parsed_accounts: ParsedAccount[];
};

export type UploadQBOFileResponse = {
  uuid: string;
  status: string;
  entity: string;
  uploaded_csv: string;
  coa_import_state: {
    coa_import_state_id: string;
    uploaded_coa_csv: string;
    uploaded_balance_sheet_csv: string;
    start_date: string;
    bank_account_names: string[];
    credit_card_names: string[];
    coa_data: ImportCOAData[];
    parsed_accounts: ParsedAccount[];
  };
};

type QBOFileObject = {
  uuid: string;
  name: string;
  is_previewable: boolean;
  is_locked: boolean;
  file_type: keyof typeof FILE_TYPE;
  type: string;
  file_size: number;
  owner: string;
  created_at: string;
};

export const BOOKS_IMPORT_STATUS = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  REVERTING: "REVERTING",
} as const;

type BOOKS_IMPORT_STATUS_TYPE = keyof typeof BOOKS_IMPORT_STATUS;

export type GetQBOFileResponse = {
  uuid: string;
  status: BOOKS_IMPORT_STATUS_TYPE;
  entity: string;
  uploaded_csv: string;
  vendor_file?: QBOFileObject;
  customer_file?: QBOFileObject;
  je_file?: QBOFileObject;
  invoice_file?: QBOFileObject;
  bill_file?: QBOFileObject;
  coa_file?: QBOFileObject;
  balance_sheet_file?: QBOFileObject;
  date_till_import: string;
  prev_fy_end_date: string;
  last_import_date: string;
  coa_import_state_id: string | null;
  default_category_mapping: Record<string, number> | null;
};

export type ImportDefaultCategory = {
  section: string;
  section_category_identifiers: number[];
  title: string;
  category_name: string;
  coa_description: string;
  category: {
    name: string;
    identifier: number;
  } | null;
};

type CoAImportState = UploadQBOFileResponse["coa_import_state"];

export type FinallyImportResponse = {
  uuid: string;
  status: BOOKS_IMPORT_STATUS_TYPE;
  source: "FINALLY";
  prev_fy_end_date: string | null;
  date_till_import: string | null;
  coa_import_state_id: string | null;
  coa_file: QBOFileObject | null;
  je_file: QBOFileObject | null;
  coa_import_state:
    | (Omit<CoAImportState, "coa_data"> & {
        coa_data: ImportCOAData[] | { data: unknown[] };
      })
    | null;
};
